import { useCallback, useState } from "react";

import {
  FileTypeEnum,
  useCreateIndependentLearnerFileMutation,
  useDeleteLearnerFileMutation,
  useMarkLearnerFileAsUploadedMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";

export interface ChatFileAttachment {
  file: File;
  fileId?: string;
  uploadProgress: number;
  status: "pending" | "uploading" | "uploaded" | "error";
  error?: string;
}

export const useChatFileUpload = (learnerId: string) => {
  const [attachment, setAttachment] = useState<ChatFileAttachment | null>(null);

  const createIndependentLearnerFileMutation = useCreateIndependentLearnerFileMutation({
    throwOnError: false,
  });

  const markLearnerFileAsUploadedMutation = useMarkLearnerFileAsUploadedMutation({
    throwOnError: false,
  });

  const deleteLearnerFileMutation = useDeleteLearnerFileMutation({
    throwOnError: false,
  });

  const uploadFileInternal = useCallback(
    async (file: File): Promise<string | null> => {
      try {
        // Update status to uploading
        setAttachment({
          file,
          uploadProgress: 0,
          status: "uploading" as const,
        });

        // Step 1: Create learner file and get signed S3 URL
        const createResult = await createIndependentLearnerFileMutation.mutateAsync({
          input: {
            fileKind: FileTypeEnum.ChatAsset,
            fileSize: file.size,
            mimeType: file.type,
            originalFileName: file.name,
            learnerId: learnerId,
          },
        });

        const learnerFile = createResult?.createIndependentLearnerFile?.learnerFile;
        const signedS3Url = learnerFile?.signedS3Url;
        const fileId = learnerFile?.id;

        if (!signedS3Url || !fileId) {
          throw new Error("Failed to get S3 upload URL");
        }

        // Update with fileId
        setAttachment((prev) => (prev ? { ...prev, fileId } : null));

        // Step 2: Upload file to S3
        const uploadResponse = await fetch(signedS3Url, {
          method: "PUT",
          headers: new Headers({ "Content-Type": file.type }),
          body: file,
        });

        if (uploadResponse.status !== 200) {
          throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
        }

        // Step 3: Mark file as uploaded
        await markLearnerFileAsUploadedMutation.mutateAsync({
          fileId,
        });

        // Update status to uploaded
        setAttachment((prev) => (prev ? { ...prev, status: "uploaded" as const, uploadProgress: 100 } : null));

        return fileId;
      } catch (error) {
        console.error("Error uploading file:", error);
        setAttachment({
          file,
          uploadProgress: 0,
          status: "error" as const,
          error: error instanceof Error ? error.message : "Upload failed",
        });
        notifyError(`Failed to upload ${file.name}`);
        return null;
      }
    },
    [learnerId, createIndependentLearnerFileMutation, markLearnerFileAsUploadedMutation],
  );

  const setAttachmentFile = useCallback(
    (file: File | null) => {
      if (file) {
        // Immediately start upload when file is selected
        uploadFileInternal(file);
      } else {
        setAttachment(null);
      }
    },
    [uploadFileInternal],
  );

  const removeAttachment = useCallback(async () => {
    // Delete file from FDM and S3 if it was uploaded
    if (attachment?.fileId) {
      try {
        await deleteLearnerFileMutation.mutateAsync({
          fileId: attachment.fileId,
        });
        setAttachment(null);
      } catch (error) {
        console.error("Error deleting file:", error);
        notifyError("Failed to delete file");
      }
    } else {
      setAttachment(null);
    }
  }, [attachment?.fileId, deleteLearnerFileMutation]);

  const clearAttachment = useCallback(() => {
    setAttachment(null);
  }, []);

  return {
    attachment,
    setAttachmentFile,
    removeAttachment,
    clearAttachment,
  };
};
